import React from 'react';
import Collapse from '@mui/material/Collapse';
import Zoom from '@mui/material/Zoom';
import Chip from '@mui/material/Chip';
import { CreateCSSProperties } from '@mui/styles/withStyles';
import DeleteIcon from '@mui/icons-material/ClearRounded';
import { styled } from '@guest-widgets/core';
import { classes } from '@guest-widgets/shared/src/classes';
import { Theme } from '@mui/material/styles';
import { useFeature } from '@guest-widgets/shared/src/contexts/featureContext/featureContext';
import { Feature } from '@guest-widgets/shared/src/features';

import type { Filters } from '../../filters';
import { FormatOption, useFormatFilterValue } from '../../useFormatFilterValue';

interface FilterChipsProps {
  filters: Filters;
  containerStyles?: CreateCSSProperties;
  chipStyles?: CreateCSSProperties;
  onDelete(key: FiltersKey): void;
}

type FiltersKey = keyof Filters;

export const FilterChips = ({
  filters,
  onDelete,
  containerStyles,
  chipStyles,
}: FilterChipsProps) => {
  const formatValue = useFormatFilterValue();
  const filterEntries = Object.entries(filters);
  const { isEnabled } = useFeature();

  const handleOnDelete = (key: FiltersKey) =>
    isEnabled(Feature.DateFilter) && key === 'dates' ? undefined : () => onDelete(key);

  return (
    <StyledCollapse in={filterEntries.length > 0}>
      <Container overrideStyles={containerStyles} className={classes.catalog.filters.applied.root}>
        {filterEntries.map(([key, value]) => (
          <Zoom key={key} in={true}>
            <StyledChip
              className={classes.catalog.filters.applied.chip}
              data-cy={`applied-filter-chip-${key}`}
              overrideStyles={chipStyles}
              variant="outlined"
              key={key}
              deleteIcon={<DeleteIcon data-cy={`applied-filter-chip-delete-${key}`} />}
              onDelete={handleOnDelete(key as FiltersKey)}
              label={formatValue[key as FiltersKey](value, FormatOption.ShortLabel)}
            />
          </Zoom>
        ))}
      </Container>
    </StyledCollapse>
  );
};

interface OverrideStyles {
  overrideStyles?: CreateCSSProperties<any>;
}

const StyledCollapse = styled(Collapse)({
  zIndex: 2,
});

const Container = styled('div')(({ overrideStyles }: OverrideStyles) => ({
  display: 'flex',
  ...overrideStyles,
}));

const StyledChip = styled(Chip)(({ theme, overrideStyles }: { theme: Theme } & OverrideStyles) => ({
  '&:not(:last-child)': {
    marginRight: theme.spacing(3),
  },
  ...overrideStyles,
}));
